import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { ArrowLeft, Save, Trash2, Eye } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const CategoryEdit = () => {
  const { id } = useParams()
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(true)
  const [categoryData, setCategoryData] = useState(null)
  const [questionCount, setQuestionCount] = useState(0)
  
  const { getCategory, updateCategory, deleteCategory, getQuestions } = useFirebase()
  const navigate = useNavigate()

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      const [category, questions] = await Promise.all([
        getCategory(id),
        getQuestions()
      ])
      
      if (!category) {
        toast.error('Category not found')
        navigate('/categories')
        return
      }
      
      setCategoryData(category)
      setFormData({
        name: category.name || '',
        description: category.description || ''
      })
      
      // Count questions in this category
      const questionsInCategory = questions.filter(q => q.category === id)
      setQuestionCount(questionsInCategory.length)
    } catch (error) {
      console.error('Error loading category:', error)
      toast.error('Failed to load category')
      navigate('/categories')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Please enter a category name')
      return
    }
    
    if (formData.name.trim().length < 2) {
      toast.error('Category name must be at least 2 characters long')
      return
    }

    setSaving(true)
    
    try {
      const updatedData = {
        name: formData.name.trim(),
        description: formData.description.trim()
      }
      
      await updateCategory(id, updatedData)
      toast.success('Category updated successfully')
      navigate('/categories')
    } catch (error) {
      console.error('Error updating category:', error)
      toast.error('Failed to update category')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (questionCount > 0) {
      toast.error('Cannot delete category that contains questions. Please move or delete the questions first.')
      return
    }

    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await deleteCategory(id)
        toast.success('Category deleted successfully')
        navigate('/categories')
      } catch (error) {
        console.error('Error deleting category:', error)
        toast.error('Failed to delete category')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Category</h1>
          <p className="text-sm text-gray-600">
            Update the category details
          </p>
        </div>
        <Link to="/categories" className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Categories
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Category Details</h3>
            </div>
            
            <div className="card-body">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Name */}
                <div>
                  <label htmlFor="name" className="form-label">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    placeholder="Enter category name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a clear, descriptive name for this category
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="form-label">
                    Description (Optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    className="form-textarea"
                    placeholder="Provide a brief description of what this category covers..."
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Help users understand what topics this category includes
                    </p>
                    <span className="text-xs text-gray-400">
                      {formData.description.length}/500
                    </span>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? (
                      <div className="flex items-center">
                        <div className="loading-spinner h-4 w-4 mr-2"></div>
                        Updating...
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Update Category
                      </>
                    )}
                  </button>
                  <Link to="/categories" className="btn-outline">
                    Cancel
                  </Link>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={questionCount > 0}
                    className={`btn-danger ${questionCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={questionCount > 0 ? 'Cannot delete category with questions' : 'Delete category'}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Category
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Category History */}
          {categoryData && (categoryData.createdAt || categoryData.updatedAt) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Category History</h3>
              </div>
              <div className="card-body">
                <div className="space-y-2 text-sm text-gray-600">
                  {categoryData.createdAt && (
                    <p>
                      <span className="font-medium">Created:</span> 
                      {' '}{new Date(categoryData.createdAt).toLocaleString()}
                      {categoryData.createdBy && (
                        <span> by {categoryData.createdBy}</span>
                      )}
                    </p>
                  )}
                  {categoryData.updatedAt && (
                    <p>
                      <span className="font-medium">Last Updated:</span> 
                      {' '}{new Date(categoryData.updatedAt).toLocaleString()}
                      {categoryData.updatedBy && (
                        <span> by {categoryData.updatedBy}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview and Usage */}
        <div className="space-y-6">
          {/* Preview Card */}
          {formData.name && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Category Preview
                  </h3>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="btn-outline text-sm"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                </div>
              </div>
              
              {showPreview && (
                <div className="card-body">
                  <div className="border-l-4 border-l-primary-500 pl-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {formData.name}
                    </h4>
                    {formData.description && (
                      <p className="text-sm text-gray-600 mb-3">
                        {formData.description}
                      </p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <span className="h-4 w-4 mr-1">📝</span>
                        {questionCount} questions
                      </span>
                      <span className="flex items-center">
                        <span className="h-4 w-4 mr-1">📅</span>
                        {new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Category Usage</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 bg-primary-50 rounded-md">
                  <div className="text-2xl font-bold text-primary-600 mb-1">
                    {questionCount}
                  </div>
                  <div className="text-sm text-gray-600">Questions in this category</div>
                </div>
                <div className="text-center p-4 bg-success-50 rounded-md">
                  <div className="text-2xl font-bold text-success-600 mb-1">0</div>
                  <div className="text-sm text-gray-600">Exam attempts</div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Manage questions in this category:</span>
                  <div className="space-x-2">
                    <Link 
                      to={`/questions?category=${id}`} 
                      className="btn-outline text-sm"
                    >
                      View Questions
                    </Link>
                    <Link 
                      to={`/questions/add?category=${id}`} 
                      className="btn-primary text-sm"
                    >
                      Add Question
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategoryEdit
